import React, { useState } from 'react';
import { Search, ExternalLink, Building2 } from 'lucide-react';

const TenantHub = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [urlToFix, setUrlToFix] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  
  // All your tenants with their links
  const tenants = [
    { name: 'Ace Chimney Sweeps Inc', shortName: 'acechimneysweeps', url: 'https://acechimneysweeps.eh.go.servicetitan.com/', color: 'from-orange-500 to-red-500' },
    { name: 'Advanced Air Systems', shortName: 'advancedairsystemsaz', url: 'https://advancedairsystemsaz.eh.go.servicetitan.com/', color: 'from-blue-500 to-cyan-500' },
    { name: 'Alco Air', shortName: 'alco', url: 'https://alco.eh.go.servicetitan.com/', color: 'from-purple-500 to-pink-500' },
    { name: 'Ambient Comfort', shortName: 'ambientcontrol', url: 'https://ambientcontrol.eh.go.servicetitan.com/', color: 'from-green-500 to-emerald-500' },
    { name: 'Cardwell HVAC & Plumbing', shortName: 'cardwellhvac', url: 'https://cardwellhvac.eh.go.servicetitan.com/', color: 'from-indigo-500 to-blue-500' },
    { name: 'Climate Control', shortName: 'climate', url: 'https://climate.eh.go.servicetitan.com/', color: 'from-teal-500 to-cyan-500' },
    { name: 'E I Electrical Inc.', shortName: 'eielectricalinc', url: 'https://eielectricalinc.eh.go.servicetitan.com/', color: 'from-yellow-500 to-orange-500' },
    { name: 'Econo Air', shortName: 'econoair', url: 'https://econoair.eh.go.servicetitan.com/', color: 'from-red-500 to-pink-500' },
    { name: 'Expert Wire', shortName: 'expertwire', url: 'https://expertwire.eh.go.servicetitan.com/', color: 'from-violet-500 to-purple-500' },
    { name: 'Infinity Texas Air', shortName: 'infinitytexasair', url: 'https://infinitytexasair.eh.go.servicetitan.com/', color: 'from-sky-500 to-blue-500' },
    { name: 'KC Waterproofing and Foundation', shortName: 'kcwaterproofinginc', url: 'https://kcwaterproofinginc.eh.go.servicetitan.com/', color: 'from-slate-500 to-gray-500' },
    { name: 'McWilliams & Son', shortName: 'mcwilliamsson', url: 'https://mcwilliamsson.eh.go.servicetitan.com/', color: 'from-amber-500 to-yellow-500' },
    { name: 'My Georgia Plumber, Inc.', shortName: 'mygeorgiaplumber', url: 'https://mygeorgiaplumber.eh.go.servicetitan.com/', color: 'from-rose-500 to-red-500' },
    { name: 'Outbound360', shortName: 'outbound360', url: 'https://outbound360.eh.go.servicetitan.com/', color: 'from-fuchsia-500 to-pink-500' },
    { name: 'Rowland Air Inc.', shortName: 'rowland_dutton', url: 'https://rowland_dutton.eh.go.servicetitan.com/', color: 'from-lime-500 to-green-500' },
    { name: 'Sky Blue Heating & Cooling', shortName: 'skybluehvac', url: 'https://skybluehvac.eh.go.servicetitan.com/', color: 'from-cyan-500 to-blue-500' },
    { name: 'Standard Heating, Cooling & Plumbing', shortName: 'standardheatingandairconditioning', url: 'https://standardheatingandairconditioning.eh.go.servicetitan.com/', color: 'from-emerald-500 to-teal-500' },
    { name: 'BCI Mechanical', shortName: 'bcimechanical', url: 'https://bcimechanical.sera.tech/admins/login', color: 'from-blue-600 to-indigo-600' },
    { name: 'Big Wave', shortName: 'bigwave', url: 'https://bigwave.sera.tech/admins/login', color: 'from-cyan-600 to-blue-600' },
    { name: 'Top Notch', shortName: 'topnotch', url: 'https://topnotch.sera.tech/admins/login', color: 'from-purple-600 to-pink-600' },
  ];

  const filteredTenants = tenants.filter(tenant =>
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.shortName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const fixUrl = () => {
    if (!urlToFix.trim()) return;
    
    // Remove the tenant subdomain pattern (e.g., alco.eh. or bcimechanical.sera.)
    const fixedUrl = urlToFix.replace(/https?:\/\/[^.]+\.(eh|sera)\./, 'https://');
    
    // Copy to clipboard
    navigator.clipboard.writeText(fixedUrl).then(() => {
      // Show notification
      setShowNotification(true);
      
      // Clear input
      setUrlToFix('');
      
      // Hide notification after 3 seconds
      setTimeout(() => {
        setShowNotification(false);
      }, 3000);
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      fixUrl();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/50 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Building2 className="text-white" size={24} />
            </div>
            <h1 className="text-2xl font-bold text-white">OB360 Hub</h1>
          </div>
          <div className="text-sm text-slate-400">
            {filteredTenants.length} {filteredTenants.length === 1 ? 'Tenant' : 'Tenants'}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8 animate-fade-in">
          <h2 className="text-3xl font-bold text-white mb-2">Welcome to OB360 Hub</h2>
          <p className="text-slate-400">Made by a coworker, for coworkers</p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Search tenants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Tenants Grid - More compact layout */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
          {filteredTenants.map((tenant, index) => (
            <a
              key={tenant.shortName}
              href={tenant.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-slate-700 hover:border-slate-600 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/20"
              style={{
                animation: `fadeInUp 0.5s ease-out ${index * 0.02}s both`
              }}
            >
              {/* Gradient Background on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${tenant.color} opacity-0 group-hover:opacity-10 rounded-lg transition-opacity duration-300`}></div>
              
              {/* Content */}
              <div className="relative">
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 bg-gradient-to-br ${tenant.color} rounded-lg flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-300`}>
                    <Building2 className="text-white" size={20} />
                  </div>
                  <ExternalLink className="text-slate-400 group-hover:text-white transition-colors" size={16} />
                </div>
                
                <h3 className="text-sm font-semibold text-white mb-1 group-hover:text-blue-400 transition-colors leading-tight">
                  {tenant.name}
                </h3>
                
                <p className="text-xs text-slate-400 font-mono truncate">
                  {tenant.shortName}
                </p>
              </div>

              {/* Shine Effect */}
              <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none overflow-hidden">
                <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
              </div>
            </a>
          ))}
        </div>

        {/* No Results */}
        {filteredTenants.length === 0 && (
          <div className="text-center py-16">
            <div className="text-slate-400 text-lg">No tenants found matching "{searchTerm}"</div>
          </div>
        )}

        {/* URL Fixer Tool */}
        <div className="mt-16 pt-12 border-t border-slate-700">
          <div className="max-w-3xl mx-auto">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-slate-700">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">URL Fixer</h3>
                  <p className="text-sm text-slate-400">Remove tenant prefix from ServiceTitan URLs</p>
                </div>
              </div>

              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Paste URL here (e.g., https://alco.eh.go.servicetitan.com/...)"
                  value={urlToFix}
                  onChange={(e) => setUrlToFix(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="flex-1 px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                />
                <button
                  onClick={fixUrl}
                  disabled={!urlToFix.trim()}
                  className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-lg hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  Fix URL
                </button>
              </div>

              <div className="mt-3 text-xs text-slate-500">
                Example: <span className="font-mono text-slate-400">alco.eh.go.servicetitan.com</span> → <span className="font-mono text-emerald-400">go.servicetitan.com</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Success Notification */}
      {showNotification && (
        <div className="fixed bottom-8 right-8 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 animate-slide-in z-50">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <div>
            <div className="font-semibold">Fixed and Copied!</div>
            <div className="text-sm text-emerald-100">URL ready to paste</div>
          </div>
        </div>
      )}

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
        
        .animate-slide-in {
          animation: slide-in 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default TenantHub;
